import { infiniteQueryOptions, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { mapResponseError } from '@mybusinessmate/api-client';
import type {
  CreateProjectInput,
  ProjectBucket,
  ProjectDomain,
  ProjectListResponse,
  ProjectResponse,
} from '@mybusinessmate/schema';
import { api } from './api';

export interface ProjectFilters {
  /** Staff name a business; a client's list is always their own, so it is left out. */
  businessId?: string;
  bucket: ProjectBucket;
  domain?: ProjectDomain;
  q?: string;
}

const PAGE_SIZE = 20;

/** Every project query hangs off this key, so one invalidate refreshes lists and details after a change. */
export const projectsKey = ['projects'] as const;

async function fetchPage(filters: ProjectFilters, cursor: string | undefined): Promise<ProjectListResponse> {
  const res = await api.api.projects.$get({
    query: {
      bucket: filters.bucket,
      limit: String(PAGE_SIZE),
      ...(filters.businessId ? { businessId: filters.businessId } : {}),
      ...(filters.domain ? { domain: filters.domain } : {}),
      ...(filters.q ? { q: filters.q } : {}),
      ...(cursor ? { cursor } : {}),
    },
  });
  if (!res.ok) throw await mapResponseError(res);
  return res.json();
}

export const projectListQuery = (filters: ProjectFilters) =>
  infiniteQueryOptions({
    queryKey: [...projectsKey, 'list', filters] as const,
    queryFn: ({ pageParam }) => fetchPage(filters, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

export const projectQuery = (id: string) =>
  queryOptions({
    queryKey: [...projectsKey, 'detail', id] as const,
    queryFn: async (): Promise<ProjectResponse> => {
      const res = await api.api.projects[':id'].$get({ param: { id } });
      if (!res.ok) throw await mapResponseError(res);
      return res.json();
    },
  });

export function useCreateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (json: CreateProjectInput): Promise<ProjectResponse> => {
      const res = await api.api.projects.$post({ json });
      if (!res.ok) throw await mapResponseError(res);
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: projectsKey }),
  });
}
